/* SpawnAnything — point costs for the campaign puzzles (pure, no DOM; shared by the campaign UI and the tests).
   Units: derived from TYPES/WEAPONS (hit points, damage, tempo). Lanchester's square law says an army's strength grows with
   N² · hp · dps, so the price per unit follows sqrt(hp · dps): two armies of equal cost are about equally strong.
   Splash (aoe) and sweeping attacks (cleave, stomp) hit a whole crowd, range lets a unit fire long before it is hit;
   both multiply the price. That formula is only the fallback: every unit the campaign uses has a MEASURED price instead
   (server/fit-campaign-costs.js: the smallest army of the type that beats a Ritter, an archer and a rifle line of the same
   points in the real simulation, geometric mean). The formula missed by up to 3x (halberdiers, tanks). Ritter + Schwert = 10.
   Tools: one charge, priced by damage and blast radius. Buildings: like a unit without legs (armour counts as extra hit points,
   guns as damage); buildings that train troops cost two units of the army they will train. */
import { TYPES, WEAPONS } from '../game/voxel.js';
import { ERA_TOOLS, ERA_TYPES, ERA_WEAPONS } from '../content/eras.js';

const ALL_TYPES = { ...TYPES, ...ERA_TYPES }, ALL_WEAPONS = { ...WEAPONS, ...ERA_WEAPONS };
const K = 0.167; // ritter + schwert = 10
const TOOL_STATS = { granate: { dmg: 140, aoe: 4.5 }, fass: { dmg: 480, aoe: 9 }, atom: { dmg: 250000, aoe: 42 }, ...ERA_TOOLS };

/** Effective damage per second of a weapon carried by a unit type: accuracy, and how many enemies one hit reaches in a crowd. */
export function unitDps(ty, wp) {
  let dps = wp.dmg * (ty.dmgMul || 1) / wp.cd;
  if (wp.hitscan) dps *= wp.acc || 1;
  if (wp.aoe) dps *= 1 + 0.9 * wp.aoe * wp.aoe;
  else if (wp.cleave || ty.stomp) { const R = (wp.reach || 0) + 0.5; dps *= 1 + 5 * R * R; }
  return dps;
}

/** Measured worth of the campaign's units ('type/weapon' -> points), from server/fit-campaign-costs.js (02.10.2026).
   Rerun it after changes to the simulation or to unit/weapon stats. */
export const MEASURED = {
  'affe/faeuste': 7, 'hoehlenmensch/keule': 8, 'ritter/schwert': 10, 'legionaer/schwert': 10, 'hoplit/speer': 12,
  'musketier/muskete': 13, 'schamane/steinwurf': 13, 'gladiator/schwert': 14, 'sanitaeter/pistole': 16,
  'hellebardier/hellebarde': 16, 'bogenschuetze/bogen': 21, 'armbrustschuetze/armbrust': 24, 'ww_soldat/karabiner': 26,
  'flammenwerfer_soldat/flammenwerfer': 27, 'saebelzahntiger/pranke': 30, 'lanzenreiter/lanze': 34,
  'astronaut/laserpistole': 37, 'mg_schuetze/mg': 38, 'streitwagen/wurfspeer': 39, 'spezialkraft/sturmgewehr': 54,
  'scharfschuetze/scharfschuetzengewehr': 56, 'cyborg/plasma': 67, 'schutzanzug/giftgaswerfer': 88,
  'roboter/lasergewehr': 96, 'robohund/lasergewehr': 105, 'alien/plasmagewehr': 160, 'mech_pilot/railgun': 185,
  'raketenschuetze/raketenwerfer_modern': 275, 'kampfelefant/stosszaehne': 285, 'trex/faeuste': 330,
  'mammut/stosszaehne': 685, 'panzer/kanone': 1740, 'weltraumwurm/faeuste': 2420
};

/** Measured matchups ('type/weapon' -> [vs melee, vs ranged]): worth against the Ritter line and against the archer / rifle
   lines, relative to the unit's overall worth (1 = as good as its price says). Same run as MEASURED. */
export const MATCHUP = {
  'affe/faeuste': [0.73, 1.17], 'alien/plasmagewehr': [1.33, 0.87], 'armbrustschuetze/armbrust': [1.27, 0.89],
  'astronaut/laserpistole': [1.65, 0.78], 'bogenschuetze/bogen': [1.15, 0.93], 'cyborg/plasma': [3.93, 0.50],
  'flammenwerfer_soldat/flammenwerfer': [3.75, 0.52], 'gladiator/schwert': [1.07, 0.97],
  'hellebardier/hellebarde': [1.95, 0.72], 'hoehlenmensch/keule': [0.96, 1.02], 'hoplit/speer': [1.28, 0.88],
  'kampfelefant/stosszaehne': [4.36, 0.48], 'lanzenreiter/lanze': [1.07, 0.97], 'legionaer/schwert': [1.11, 0.95],
  'mammut/stosszaehne': [2.08, 0.69], 'mech_pilot/railgun': [0.94, 1.03], 'mg_schuetze/mg': [1.63, 0.78],
  'musketier/muskete': [0.92, 1.04], 'panzer/kanone': [0.91, 1.05], 'raketenschuetze/raketenwerfer_modern': [1.26, 0.89],
  'ritter/schwert': [1.11, 0.95], 'robohund/lasergewehr': [1.02, 0.99], 'roboter/lasergewehr': [1.06, 0.97],
  'saebelzahntiger/pranke': [0.96, 1.02], 'sanitaeter/pistole': [1.29, 0.88], 'schamane/steinwurf': [1.36, 0.86],
  'scharfschuetze/scharfschuetzengewehr': [0.97, 1.01], 'schutzanzug/giftgaswerfer': [1.88, 0.73],
  'spezialkraft/sturmgewehr': [1.20, 0.91], 'streitwagen/wurfspeer': [0.92, 1.04], 'trex/faeuste': [1.71, 0.76],
  'weltraumwurm/faeuste': [0.89, 1.06], 'ww_soldat/karabiner': [1.05, 0.98]
};
const STRONG = 1.35, WEAK = 0.74;

/** Short hints for the campaign cards: splash damage and measured counters. [{ key, label, icon, tone: 'good'|'bad'|'' }] */
export function unitTraits(type, weapon) {
  const ty = ALL_TYPES[type]; if (!ty) return [];
  const wk = ALL_WEAPONS[weapon] ? weapon : ty.weapon, wp = ALL_WEAPONS[wk] || {};
  const out = [];
  if (wp.aoe || wp.cleave || ty.stomp) out.push({ key: 'splash', label: 'Flächenschaden', icon: 'ti-flame', tone: '' });
  const m = MATCHUP[type + '/' + wk];
  if (m) {
    if (m[0] >= STRONG) out.push({ key: 'vsMeleeStrong', label: 'stark gegen Nahkampf', icon: 'ti-sword', tone: 'good' });
    if (m[0] <= WEAK) out.push({ key: 'vsMeleeWeak', label: 'schwach gegen Nahkampf', icon: 'ti-sword', tone: 'bad' });
    if (m[1] >= STRONG) out.push({ key: 'vsRangedStrong', label: 'stark gegen Schützen', icon: 'ti-target-arrow', tone: 'good' });
    if (m[1] <= WEAK) out.push({ key: 'vsRangedWeak', label: 'schwach gegen Schützen', icon: 'ti-target-arrow', tone: 'bad' });
  }
  return out;
}

/** Points for one unit of type `type` with weapon `weapon` (default: its standard weapon) at size 1. */
export function unitCost(type, weapon) {
  const ty = ALL_TYPES[type]; if (!ty) return 0;
  const wp = ALL_WEAPONS[weapon || ty.weapon] || ALL_WEAPONS[ty.weapon]; if (!wp) return 0;
  const m = MEASURED[type + '/' + (ALL_WEAPONS[weapon] ? weapon : ty.weapon)]; if (m) return m;
  return formulaCost(ty, wp);
}
/** The strength formula (fallback for unmeasured types and weapons). */
export function formulaCost(ty, wp) {
  const reach = wp.melee ? 1 : 2.5 + Math.min(60, wp.range || 0) / 20;
  const tempo = 0.75 + ty.speed / 12;
  const support = (ty.heal ? 3 : 1) * (ty.aura ? 2.5 : 1); // healers / aura units are worth more than their blows (Phase D4)
  return Math.max(1, Math.round(Math.sqrt(ty.hp * unitDps(ty, wp)) * reach * tempo * K * support));
}

/** Points for one charge of a tool (granate, katapult, …). */
export function toolCost(key) {
  const t = TOOL_STATS[key]; if (!t) return 0;
  return Math.max(5, Math.round(Math.sqrt(Math.min(2000, t.dmg)) * t.aoe * 1.1 / 5) * 5);
}

/** Points for a building from its level-1 sim data (buildingSimData); trainers are priced in units of the army they train. */
export function buildingCost(lv, armyUnitCost = 10) {
  if (!lv) return 0;
  const hpEff = lv.hp / Math.max(0.05, 1 - (lv.armor || 0));
  let dps = 0, range = 0;
  for (const g of lv.guns || []) { dps += g.dmg / g.cd * (g.hitscan ? g.acc || 1 : 1) * (1 + (g.aoe || 0) * 0.45); range = Math.max(range, g.range); }
  const wall = Math.sqrt(hpEff) * 0.6;
  const guns = dps ? Math.sqrt(hpEff * dps) * (2.5 + Math.min(60, range) / 20) * 0.75 * K : 0;
  const cost = wall + guns + (lv.trains || 0) * armyUnitCost * 2 + (lv.cap || 0) * 0.5;
  return Math.max(5, Math.round(cost / 5) * 5);
}

/** Cost table of every unit type with its standard weapon (for the UI and the README). */
export const UNIT_COSTS = Object.fromEntries(Object.keys(ALL_TYPES).map(k => [k, unitCost(k)]));
